import { captureEvent, isProductionSite } from "./posthog";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;

declare global {
  interface Window {
    gtag(...args: unknown[]): void;
    dataLayer: unknown[];
    clarity(...args: unknown[]): void;
  }
}

export function initGA(): void {
  if (!GA_ID || !isProductionSite()) return;

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Must use `arguments` (not spread) — GA4 queue processor checks
  // Object.prototype.toString.call(item) === '[object Arguments]'
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function () { window.dataLayer.push(arguments); } as Window["gtag"];
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false });
}

export function initClarity(): void {
  if (!CLARITY_ID || !isProductionSite()) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = window as any;
  c.clarity = c.clarity || function (...args: unknown[]) { (c.clarity.q = c.clarity.q || []).push(args); };

  const script = document.createElement("script");
  script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

export function send(event: string, params?: Record<string, string | number | boolean>): void {
  captureEvent(event, params);
  if (!GA_ID || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", event, params);
  } catch (e) {
    console.error("[analytics] gtag call failed:", e);
  }
}

export function trackPageview(path: string): void {
  if (!GA_ID || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", { page_path: path });
}

// ── Ladder ────────────────────────────────────────────────────────────────────

export function trackScoreSubmitted(ladderSlug: string, mode: "submit" | "edit" | "admin"): void {
  send("score_submitted", { ladder_slug: ladderSlug, mode });
}

/** Sanur ladder funnel: register, then agree a time, then confirm it. One event per step so a
 *  PostHog funnel can chain them. Fired from mutation onSuccess, so a failed call never counts. */
export type LadderStep =
  | "registered"
  | "time_proposed"
  | "time_said_yes"
  | "time_set"
  | "time_confirmed"
  | "time_declined"
  | "time_countered"
  | "fixture_cancelled";

export function trackLadderStep(
  step: LadderStep,
  params?: Record<string, string | number | boolean>,
): void {
  send(`ladder_${step}`, params);
}

// ── Public event ──────────────────────────────────────────────────────────────

export function trackEventView(slug: string, eventName: string, format: string): void {
  send("event_view", { event_slug: slug, event_name: eventName, event_format: format });
}

// ── Player pages ──────────────────────────────────────────────────────────────

export function trackPlayerSelect(playerName: string): void {
  send("player_select", { player_name: playerName });
}

export function trackPlayerCommunitySelect(communityName: string): void {
  send("player_community_select", { community_name: communityName });
}

export function trackPlayerTabChange(tab: string): void {
  send("player_tab_change", { tab });
}

export function trackPlayerStandingsViewChange(view: string): void {
  send("player_standings_view", { view });
}

export function trackPlayerRatingSystemChange(system: "elo" | "glicko"): void {
  send("player_rating_system", { system });
}

export function trackPlayerNarrativeRequest(): void {
  send("player_narrative_request");
}

export function trackLeaderboardRatingChange(system: "elo" | "glicko"): void {
  send("leaderboard_rating_system", { system });
}

// ── Organizer wizard ──────────────────────────────────────────────────────────

export function trackWizardStepEnter(stepId: string, stepIndex: number): void {
  send("wizard_step_enter", { step_id: stepId, step_index: stepIndex });
}

export function trackWizardFormatSelect(format: string): void {
  send("wizard_format_select", { format });
}

export function trackWizardEventCreated(format: string): void {
  send("wizard_event_created", { format });
}
